import type { WebSite, SearchAction, EntryPoint } from 'schema-dts';

import { config } from './config';
import type {
    EnhancedWebSiteInput,
    EnhancedWebSiteResult,
    WebSiteValidationError,
    GeneratorOptions,
    WebSiteSearchInput,
} from './types/company';
import { stripHtmlTags, Result, ok, fail, isOk, safeString, isValidUrl, LRUCache } from './utils';
import { logger } from './utils/logger';

const websiteCache = new LRUCache<string, WebSite>({
    maxEntries: config.getConfig().cache.maxDurationEntries,
});

function validateWebSiteSearchInput(search: unknown): Result<WebSiteSearchInput, WebSiteValidationError> {
    if (!search || typeof search !== 'object') {
        return fail({
            field: 'searchUrl',
            message: 'Search configuration must be an object',
        });
    }

    const searchObj = search as Record<string, unknown>;

    if (!searchObj.searchUrl || typeof searchObj.searchUrl !== 'string') {
        return fail({
            field: 'searchUrl',
            message: 'Search URL is required and must be a string',
            value: searchObj.searchUrl,
        });
    }

    if (!searchObj.queryParam || typeof searchObj.queryParam !== 'string') {
        return fail({
            field: 'queryParam',
            message: 'Query parameter name is required and must be a string',
            value: searchObj.queryParam,
        });
    }

    return ok({
        searchUrl: safeString(searchObj.searchUrl),
        queryParam: safeString(searchObj.queryParam),
        name: searchObj.name ? safeString(searchObj.name) : undefined,
        description: searchObj.description ? stripHtmlTags(safeString(searchObj.description)) : undefined,
    });
}

function validateEnhancedWebSiteInput(input: unknown): Result<EnhancedWebSiteInput, WebSiteValidationError[]> {
    const errors: WebSiteValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'name', message: 'WebSite input must be an object' }]);
    }

    const website = input as Record<string, unknown>;

    if (!website.name || typeof website.name !== 'string' || !website.name.trim()) {
        errors.push({
            field: 'name',
            message: 'Website name is required and must be a non-empty string',
            value: website.name,
        });
    }

    if (!website.url || typeof website.url !== 'string') {
        errors.push({
            field: 'url',
            message: 'Website URL is required and must be a string',
            value: website.url,
        });
    } else {
        if (!isValidUrl(website.url.toString())) {
            errors.push({
                field: 'url',
                message: 'Website URL must be a valid URL',
                value: website.url,
            });
        }
    }

    if (website.search) {
        const searchValidation = validateWebSiteSearchInput(website.search);
        if (!isOk(searchValidation)) {
            errors.push(searchValidation.error);
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok({
        name: safeString(website.name).trim(),
        url: safeString(website.url),
        description: website.description ? stripHtmlTags(safeString(website.description)) : undefined,
        search: website.search
            ? (() => {
                  const searchValidation = validateWebSiteSearchInput(website.search);
                  return isOk(searchValidation) ? searchValidation.data : undefined;
              })()
            : undefined,
        publisher: website.publisher ? safeString(website.publisher) : undefined,
    });
}

class EnhancedWebSiteBuilder {
    private website: Partial<WebSite>;

    constructor(name: string, url: string) {
        this.website = {
            '@type': 'WebSite',
            '@id': `${url}/#website`,
            name: safeString(name).trim(),
            url: safeString(url),
        };
    }

    setDescription(description: string): EnhancedWebSiteBuilder {
        if (description) {
            this.website.description = stripHtmlTags(safeString(description));
        }
        return this;
    }

    setSearch(searchUrl: string, queryParam: string): EnhancedWebSiteBuilder {
        if (searchUrl && queryParam) {
            // Only add search action if the base URL exists or searchUrl is absolute
            if (!this.website.url && !searchUrl.startsWith('http')) {
                logger.warn('Cannot set search action without base URL for relative search URL', { searchUrl });
                return this;
            }

            const baseUrl = (this.website.url as string | undefined) ?? '';
            const fullSearchUrl = searchUrl.startsWith('http') ? searchUrl : `${baseUrl}${searchUrl}`;

            const searchAction: SearchAction = {
                '@type': 'SearchAction',
                target: {
                    '@type': 'EntryPoint',
                    urlTemplate: `${fullSearchUrl}?${queryParam}={search_term_string}`,
                } as EntryPoint,
                ...{ 'query-input': 'required name=search_term_string' },
            };

            this.website.potentialAction = searchAction;
        }
        return this;
    }

    setPublisher(organizationName: string): EnhancedWebSiteBuilder {
        if (organizationName) {
            if (organizationName === 'Storyblocks') {
                this.website.publisher = config.getOrganizationSchema();
            } else {
                this.website.publisher = {
                    '@type': 'Organization',
                    name: safeString(organizationName),
                };
            }
        }
        return this;
    }

    build(): WebSite {
        if (!this.website.name || !this.website.url) {
            throw new Error('WebSite must have name and URL');
        }

        return this.website as WebSite;
    }
}

export function generateEnhancedWebSiteSchema(
    input: EnhancedWebSiteInput,
    options: GeneratorOptions = {},
): Result<EnhancedWebSiteResult> {
    const { useCache = true, validateInput = true } = options;

    const cacheKey = useCache ? JSON.stringify(input) : '';

    if (useCache && cacheKey) {
        const cached = websiteCache.get(cacheKey);
        if (cached) {
            logger.debug('Enhanced WebSite schema cache hit', { input });
            return ok(cached);
        }
    }

    if (validateInput) {
        const validation = validateEnhancedWebSiteInput(input);
        if (!isOk(validation)) {
            logger.warn('Invalid enhanced WebSite input', { errors: validation.error });
            return fail(new Error(`Invalid WebSite input: ${validation.error.map((e) => e.message).join(', ')}`));
        }
    }

    try {
        const builder = new EnhancedWebSiteBuilder(input.name, input.url);

        if (input.description) {
            builder.setDescription(input.description);
        }

        if (input.search) {
            builder.setSearch(input.search.searchUrl, input.search.queryParam);
        }

        if (input.publisher) {
            builder.setPublisher(input.publisher);
        }

        const website = builder.build();

        if (useCache && cacheKey) {
            websiteCache.set(cacheKey, website);
        }

        logger.info('Generated enhanced WebSite schema', {
            name: input.name,
            url: input.url,
            hasSearch: !!input.search,
            hasPublisher: !!input.publisher,
            cached: useCache,
        });

        return ok(website);
    } catch (error) {
        logger.error('Failed to generate enhanced WebSite schema', { error, input });
        return fail(error instanceof Error ? error : new Error('Unknown error generating WebSite schema'));
    }
}
